/**
 * estimate (견적) orval 래퍼
 *
 * 견적서 API는 InputItem[] 기반 쿼리, Zod 스키마 검증 등 복잡한 로직이 있으므로
 * customInstance를 직접 사용하여 기존 SDK와 동일한 인터페이스를 제공한다.
 */
import { customInstance } from "../custom-instance";
import { createUrlWithQueryByInputItem } from "@erp/shared/utils/https/create-url-with-query";
import { trimObjectArray } from "@erp/shared/utils/format/trim";
import { CalcCostDetailDataSchema } from "@erp/shared/components/ui/Document/CalcCost/const";
import { LaborCostEntrySchema } from "@erp/shared/components/ui/Dialog/LaborCostDialog/labor-cost-entry.types";
import { validateArrayWithSchema, validateWithSchema, } from "@erp/shared/utils/https/zod-validation";
import { z } from "zod";
import { GetBOMListResponseSchema } from "./product";
import { DefaultApprovalStatusSchema, TempApprovalStatusSchema, } from "./default-schemes";
// ── 스키마 & 타입 (EstimateConfirm) ──
export const GetEstimateConfirmResponseSchema = z
    .object({
    ...DefaultApprovalStatusSchema.shape,
    ...TempApprovalStatusSchema.shape,
    id: z.coerce.number(),
    writeType: z.union([z.literal(0), z.literal(1)]),
    createrPhoneNumber: z.string().nullable(),
    inhouseBidNumber: z.string().nullable(),
    companyBidNumber: z.string().nullable(),
    totalPrice: z.number(),
    companyId: z.number().nullable(),
    companyName: z.string().nullable(),
    managerId: z.number().nullable(),
    managerName: z.string().nullable(),
    managerEmail: z.string().nullable(),
    managerPhone: z.string().nullable(),
    projectId: z.coerce.number().nullable(),
    projectCode: z.string().nullable(),
    estimateCalcHistoryId: z.coerce.number(),
    estimateLaborHistoryId: z.coerce.number().nullable(),
    estimateBomHistoryId: z.coerce.number(),
    projectIsPreOrder: z.number().nullable(),
    dueDate: z.string().nullable(),
    projectName: z.string().nullable(),
    projectPeriod: z.string().nullable(),
    remark: z.string().nullable(),
    approvalFile: z.string().nullable(),
    blueprintFile: z.string().nullable(),
    etcFiles: z.array(z.string()).nullable(),
    version: z.coerce.number(),
})
    .strict();
// ── 스키마 & 타입 (EstimateConfirmDetail) ──
export const EstimateDetailInfoSchema = z
    .object({
    ...GetEstimateConfirmResponseSchema.shape,
})
    .strict();
export const BOMInfoSchema = z
    .object({
    createdTime: z.string(),
    modifiedTime: z.string(),
    creater: z.coerce.number(),
    modifier: z.coerce.number(),
    deletedYn: z.coerce.number(),
    id: z.coerce.number(),
    originId: z.coerce.number(),
    historyType: z.string(),
    historyReason: z.string().nullable(),
    bomJson: z.array(GetBOMListResponseSchema),
})
    .strict();
export const GetEstimateConfirmDetailResponseSchema = z
    .object({
    info: EstimateDetailInfoSchema,
    calc: z.array(CalcCostDetailDataSchema),
    labor: z.array(LaborCostEntrySchema),
    bom: BOMInfoSchema.nullable(),
})
    .strict();
// ── 스키마 & 타입 (HistoryVersions) ──
export const GetEstimateHistoryVersionsResponseSchema = EstimateDetailInfoSchema.extend({
    historyType: z.string(),
    historyReason: z.string().nullable(),
    originId: z.coerce.number(),
}).strict();
// ── 스키마 & 타입 (HistoryVersionData) ──
export const GetEstimateHistoryVersionDataResponseSchema = GetEstimateConfirmDetailResponseSchema.extend({
    info: EstimateDetailInfoSchema.extend({
        originId: z.coerce.number(),
        historyType: z.string(),
        historyReason: z.string().nullable(),
    }),
}).strict();
// ── API 함수 ──
export async function getEstimateConfirm(searchCardInputs) {
    const inputs = trimObjectArray(searchCardInputs);
    const url = createUrlWithQueryByInputItem("/estimate", inputs);
    const result = await customInstance({
        url,
        method: "GET",
    });
    return validateArrayWithSchema(GetEstimateConfirmResponseSchema, result);
}
export async function searchEstimateWithReject(searchCardInputs) {
    const inputs = trimObjectArray(searchCardInputs);
    const url = createUrlWithQueryByInputItem("/estimate?withRejected=true", inputs);
    const result = await customInstance({
        url,
        method: "GET",
    });
    return validateArrayWithSchema(GetEstimateConfirmResponseSchema, result);
}
export async function getEstimateConfirmDetail(id) {
    const res = await customInstance({
        url: "/estimate/" + id,
        method: "GET",
    });
    return validateWithSchema(GetEstimateConfirmDetailResponseSchema, res);
}
export const postEstimateConfirm = async (body) => {
    return customInstance({
        url: "/estimate",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: body,
    });
};
export const patchEstimateConfirm = async (id, body) => {
    return customInstance({
        url: `/estimate/${id}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: body,
    });
};
export async function patchEstimateConfirmApprove(estimateConfirmId, approvalPhase, rejectReason) {
    return customInstance({
        url: `/estimate/approve/${estimateConfirmId}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: {
            approvalPhase,
            rejectReason: rejectReason || null,
        },
    });
}
export async function checkEstimateDuplicate(inhouseBidNumber, excludeId) {
    const params = { inhouseBidNumber };
    if (excludeId != null) {
        params.excludeId = excludeId;
    }
    const response = await customInstance({
        url: "/estimate/check-duplicate",
        method: "GET",
        params,
    });
    return response.exists;
}
export async function getEstimateHistoryVersions(originId) {
    const data = await customInstance({
        url: `/estimate/${originId}/history`,
        method: "GET",
    });
    if (!data) {
        throw new Error("Failed to fetch data for the selected version.");
    }
    return validateArrayWithSchema(GetEstimateHistoryVersionsResponseSchema, data);
}
export async function getEstimateHistoryVersionData(historyId) {
    const data = await customInstance({
        url: `/estimate/history/${historyId}`,
        method: "GET",
    });
    return validateWithSchema(GetEstimateHistoryVersionDataResponseSchema, data);
}
// ── SDK 객체 ──
export const estimateApi = {
    getEstimateConfirm,
    searchEstimateWithReject,
    getEstimateConfirmDetail,
    postEstimateConfirm,
    patchEstimateConfirm,
    patchEstimateConfirmApprove,
    checkEstimateDuplicate,
    getEstimateHistoryVersions,
    getEstimateHistoryVersionData,
};
